import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { omit } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";

import Stepper from "common/Stepper";
import AuthorizedPersonDetails from "Components/AuthorizedPersonDetails/AuthorizedPersonDetails";
import DocumentToSubmit from "Components/DocumentsToSubmit";
import Declaration from "Components/Declaration";
import BreadCrumb from "Components/Common/BreadCrumb";
import { applicationTypes, minHtContractDemandLoad } from "common/constant";
import DemandExtensionForm from "./DemandExtensionForm";
import Backdrop from "Components/Common/Backdrop";

import { setValues } from "slices/demandAlteration/reducer";
import { useValidationSchemaForDemandAlteration } from "hooks";

import { getIsDocumentRequired } from "utils/getIsDocumentRequired";
import {
  convertDocumentKeys,
  filterEmptyStrings,
  generateFormData,
} from "utils";

import {
  useLazyApplicationDetailQuery,
  usePostApplicationDetailsMutation,
} from "apis/ApplicationRequestDetail";
import { useLazyConsumerByIdQuery } from "apis/User";
import { myRequestRoute } from "Routes/constants";

const DemandExtension = ({ applicationId, handleClose = () => {}, t }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const navigate = useNavigate();

  const [submitApplication, { isLoading }] =
    usePostApplicationDetailsMutation();

  const [
    getUtilityConsumerDetails,
    {
      data: { data: customerDetails } = {},
      isLoading: isConsumerDetailsLoading,
      isFetching: isConsumerDataFetching,
    },
  ] = useLazyConsumerByIdQuery();

  const [
    getApplicationDetails,
    {
      isLoading: isApplicationDataLoading,
      isFetching: isApplicationDataFetching,
    },
  ] = useLazyApplicationDetailQuery();

  const getValidationSchema = useValidationSchemaForDemandAlteration(true);
  const user = useSelector((state) => state.Login.user);

  const [applicationData, setApplicationData] = useState({});

  const dispatch = useDispatch();

  const initialValues = useMemo(
    () => ({
      id: applicationId ?? 0,
      utilityId: 1,
      applicationTypeId: applicationTypes["Demand Extension"],
      utilityConsumerId:
        customerDetails?.id ?? applicationData?.utilityConsumerId ?? "",
      consumerName:
        applicationData?.consumerName ?? customerDetails?.consumerName ?? "",
      tariffName:
        applicationData?.tariffName ?? customerDetails?.tariffName ?? "",
      consumerNumber:
        applicationData?.consumerNumber ??
        customerDetails?.consumerNumber ??
        "",
      applicant: {
        id:
          applicationData?.applicant?.id ?? customerDetails?.applicant?.id ?? 0,
        applicantTypeId: customerDetails?.applicant?.applicantType?.id ?? "",
        applicantName: customerDetails?.applicant?.applicantName ?? "",
        panNumber:
          applicationData?.applicant?.panNumber ??
          customerDetails?.applicant?.panNumber ??
          "",
        gstNumber:
          applicationData?.applicant?.gstNumber ??
          customerDetails?.applicant?.gstNumber ??
          "",
        occupancyTypeId: customerDetails?.applicant?.occupancyType?.id ?? "",
        isGSTRegistered:
          applicationData?.applicant?.isGSTRegistered ??
          customerDetails?.applicant?.isGSTRegistered ??
          "",
        addressType: customerDetails?.applicant?.addressType ?? "",
        correspondenceAddress:
          customerDetails?.applicant?.correspondenceAddress ?? "",
        city: customerDetails?.applicant?.city ?? "",
        pinCode: customerDetails?.applicant?.pinCode ?? "",
        email: customerDetails?.applicant?.email ?? "",
        // isEmailVerified: !!applicationData?.applicant?.email ?? false,
        mobileNumber: customerDetails?.applicant?.mobileNumber ?? "",
        landLine: customerDetails?.applicant?.landLine ?? "",
        alternateEmail: customerDetails?.applicant?.alternateEmail
          ? customerDetails?.applicant?.alternateEmail?.split(", ")
          : [],
        alternateMobileNumber:
          customerDetails?.applicant?.alternateMobileNumber ?? "",
        eBillConfirmation: true,
      },
      authorizedPerson: {
        isAuthorizedPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email ===
              applicationData.user.email &&
            applicationData.authorizedPerson.mobileNumber ===
              applicationData.user.mobileNumber
          : true,
        isEditablePerson:
          applicationData?.authorizedPerson?.isEditablePerson ?? false,
        id: applicationData?.authorizedPerson?.id ?? 0,
        salutation: applicationData?.authorizedPerson?.salutation ?? "",
        firstName: applicationData?.authorizedPerson?.firstName ?? "",
        middleName: applicationData?.authorizedPerson?.middleName ?? "",
        lastName: applicationData?.authorizedPerson?.lastName ?? "",
        // gender: applicationData?.authorizedPerson?.gender ?? "",
        // ageCategory: applicationData?.authorizedPerson?.ageCategory ?? "",
        designation: applicationData?.authorizedPerson?.designation ?? "",
        email: applicationData?.authorizedPerson?.email ?? "",
        mobileNumber: applicationData?.authorizedPerson?.mobileNumber ?? "",
        landLine: applicationData?.authorizedPerson?.landLine ?? "",
        image: "",
        imageUrl: applicationData?.authorizedPerson?.imageUrl ?? "",
        isUnRegisteredPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email !== user.email &&
            applicationData.authorizedPerson.mobileNumber !== user.mobileNumber
          : false,
      },
      utilityAddress: {
        premisesAreaId: customerDetails?.utilityAddress?.premiseArea?.id ?? "",
      },
      connectionLoad: {
        id: applicationData?.connectionLoad?.id ?? 0,
        backupRequired:
          !!applicationData?.connectionLoad?.backupkVA ||
          !!customerDetails?.connectionLoad?.backupkVA,
        backupkVA:
          applicationData?.connectionLoad?.backupkVA ??
          customerDetails?.connectionLoad?.backupkVA ??
          "",
        powerRequirementUnit:
          (applicationData?.connectionLoad?.totalPowerRequirement ??
            customerDetails?.connectionLoad?.totalPowerRequirement) <= 99
            ? 1
            : 2 ?? "",
        loadDetails:
          applicationData?.connectionLoad?.loadDetail ??
          customerDetails?.connectionLoad?.loadDetail ??
          "",
        supplyVoltage: +applicationData?.connectionLoad?.supplyVoltage
          ? +applicationData.connectionLoad.supplyVoltage
          : +customerDetails?.connectionLoad?.supplyVoltage
          ? +customerDetails.connectionLoad.supplyVoltage
          : "",
        supplyVoltageUnit:
          applicationData?.connectionLoad?.supplyVoltageUnit ??
          customerDetails?.connectionLoad?.supplyVoltageUnit ??
          "",
        powerRequirement:
          applicationData?.connectionLoad?.powerRequirement ??
          customerDetails?.connectionLoad?.totalPowerRequirement ??
          "",
        timeShift:
          applicationData?.connectionLoad?.timeShiftEnum ??
          customerDetails?.connectionLoad?.timeShiftEnum ??
          "",
        tention: applicationData?.connectionLoad?.totalPowerRequirement
          ? applicationData.connectionLoad.totalPowerRequirement <
            minHtContractDemandLoad
            ? "LT"
            : "HT"
          : customerDetails?.connectionLoad?.totalPowerRequirement
          ? customerDetails.connectionLoad.totalPowerRequirement <
            minHtContractDemandLoad
            ? "LT"
            : "HT"
          : "",
        isGreenPowerRequired: !!customerDetails?.connectionLoad?.greenPower,
        greenPower:
          applicationData?.connectionLoad?.greenPower ??
          customerDetails?.connectionLoad?.greenPower ??
          "",
        averageConsumption:
          applicationData?.connectionLoad?.averageConsumption ??
          customerDetails?.connectionLoad?.averageConsumption ??
          "",
        increaseOfLoad:
          applicationData?.connectionLoad?.increaseOfLoad ??
          customerDetails?.connectionLoad?.increaseOfLoad ??
          "",
        totalPowerRequirement:
          applicationData?.connectionLoad?.totalPowerRequirement ??
          customerDetails?.connectionLoad?.totalPowerRequirement ??
          0,
      },

      declaration: {
        declarationId: applicationData?.declaration?.id ?? 0,
        isConsumerDuesOutstanding:
          !!applicationData?.declaration?.consumerDuesOutstanding ?? false,
        consumerDuesOutstanding:
          applicationData?.declaration?.consumerDuesOutstanding ?? "",
        isPremisesDuesOutstanding:
          !!applicationData?.declaration?.premisesDuesOutstanding ?? false,
        premisesDuesOutstanding:
          applicationData?.declaration?.premisesDuesOutstanding ?? "",
        isFirmDuesOutstanding:
          !!applicationData?.declaration?.firmDuesOutstanding ?? false,
        firmDuesOutstanding:
          applicationData?.declaration?.firmDuesOutstanding ?? "",
      },
      documents: applicationData?.documents?.filter(({ id }) => id) ?? [],
      extraValues: {
        premiseTypeId: customerDetails?.utilityAddress?.premiseType?.id ?? "",
        premiseUsagePurpose:
          customerDetails?.utilityAddress?.premiseUsagePurpose ?? "",
        requiredDocumentsCount: 0,
        isPanNumber: !!customerDetails?.applicant?.panNumber,
        isGstNumber: !!customerDetails?.applicant?.gstNumber,
        userMobileNumber: applicationData?.user?.mobileNumber ?? "",
        userEmail: applicationData?.user?.email ?? "",
      },
      inProgressApplicationId: customerDetails?.inProgressApplicationId ?? 0,
      isAllApplicationsProcessCompleted:
        customerDetails?.isAllApplicationsProcessCompleted ?? false,
      payableBillAmount: customerDetails?.payableBillAmount ?? 0,
      isIGSTExempted: customerDetails?.isIGSTExempted ?? false,
    }),
    [applicationData, customerDetails]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: !(
      (customerDetails?.inProgressApplicationId !== applicationData?.id &&
        !customerDetails?.isAllApplicationsProcessCompleted) ||
      customerDetails?.payableBillAmount > 0
    )
      ? getValidationSchema(currentStep)
      : Yup.object({}),

    onSubmit: async (values, actions) => {
      if (currentStep === steps.length - 1) {
        createDemandExtension(values);
      } else {
        if (
          values?.inProgressApplicationId !== applicationData?.id &&
          !values.isAllApplicationsProcessCompleted
        ) {
          toast.error(
            "There are some incomplete applications associated with this consumer number. You are unable to fill the form."
          );
        } else if (values.payableBillAmount > 0) {
          toast.error(
            `You have an unpaid amount of ${values.payableBillAmount} RS. Please pay first to continue.`
          );
        } else if (
          values.connectionLoad.totalPowerRequirement ===
          customerDetails?.connectionLoad?.totalPowerRequirement
        ) {
          toast.warning("Please enter how much load you want to increase");
        } else {
          setCurrentStep((prev) => prev + 1);
          actions.setTouched({});
        }
      }
    },
  });

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: "Demand Extension",
        isCompleted: currentStep >= 1,
        component: <DemandExtensionForm formik={formik} />,
      },
      {
        id: 2,
        name: "Authorized Person Details",
        isCompleted: currentStep >= 2,
        component: <AuthorizedPersonDetails formik={formik} />,
      },
      {
        id: 3,
        name: "Document to Submit",
        isCompleted: currentStep >= 3,
        component: <DocumentToSubmit formik={formik} />,
      },
      {
        id: 4,
        name: "Declaration",
        isCompleted: currentStep === 4,
        component: <Declaration formik={formik} />,
      },
    ],
    [currentStep, formik]
  );

  const { values, initialValues: formikInitialValues, handleSubmit } = formik;

  const {
    documents,
    extraValues: { requiredDocumentsCount },
  } = values;

  const getCustomerDetails = async (utilityConsumerId) => {
    try {
      await getUtilityConsumerDetails(utilityConsumerId);
      if (
        applicationId &&
        values.utilityConsumerId !== formikInitialValues.utilityConsumerId
      ) {
        setApplicationData({});
      }
    } catch (error) {}
  };

  const getApplicationData = async (applicationId) => {
    try {
      const res = await getApplicationDetails(applicationId);
      setApplicationData(res?.data);
    } catch (error) {}
  };

  useEffect(() => {
    if (applicationId) {
      getApplicationData(applicationId);
      // setCompletedSteps(steps.length);
    }
  }, [applicationId]);

  useEffect(() => {
    if (values.utilityConsumerId) {
      getCustomerDetails(values.utilityConsumerId);
    }
  }, [values.utilityConsumerId]);

  useEffect(() => {
    dispatch(setValues(formik.values));
  }, [values, dispatch]);

  const validateDocument = () => {
    const requiredUploadedDocumentsCount = documents.filter(
      ({ documentRequiredEnum }) => getIsDocumentRequired(documentRequiredEnum)
    ).length;

    if (
      currentStep === 2 &&
      requiredUploadedDocumentsCount !== requiredDocumentsCount
    ) {
      toast.error("Please upload all required documents");
    }
  };

  const createDemandExtension = async (values) => {
    const imageKeyToRemove = values.authorizedPerson.image
      ? "authorizedPerson.imageUrl"
      : "authorizedPerson.image";
    const cityId = values.applicant.city?.id ?? "";
    const id = values.applicant.id;

    // converts document array to key-value pair
    const payloadWithUpdatedDocumentKeys = convertDocumentKeys(values);

    const applicationDetails = omit(payloadWithUpdatedDocumentKeys, [
      "tariffName",
      "applicant.city",
      "consumerName",
      "inProgressApplicationId",
      "isAllApplicationsProcessCompleted",
      "payableBillAmount",
      "connectionLoad.isGreenPowerRequired",
      "connectionLoad.tention",
      "consumerNumber",
      "connectionLoad.backupRequired",
      "connectionLoad.totalPowerRequirement",
      "authorizedPerson.isAuthorizedPerson",
      "authorizedPerson.isUnRegisteredPerson",
      "extraValues",
      "utilityAddress",
      "declaration.isConsumerDuesOutstanding",
      "declaration.isPremisesDuesOutstanding",
      "declaration.isFirmDuesOutstanding",
      "authorizedPerson.isEditablePerson",
      "documents",
      "isIGSTExempted",
      imageKeyToRemove,
    ]);

    const currentGstNumber = applicationId
      ? applicationData?.applicant?.gstNumber
      : customerDetails?.applicant?.gstNumber;

    const currentPanNumber = applicationId
      ? applicationData?.applicant?.panNumber
      : customerDetails?.applicant?.panNumber;

    const filteredValues = filterEmptyStrings({
      ...applicationDetails,
      applicant: {
        ...applicationDetails.applicant,
        alternateEmail: applicationDetails.applicant.alternateEmail.length
          ? applicationDetails.applicant.alternateEmail.join(", ")
          : "",
        cityId,
        id:
          applicationDetails.applicant.gstNumber !== (currentGstNumber ?? "") ||
          applicationDetails.applicant.panNumber !== (currentPanNumber ?? "")
            ? 0
            : id,
      },
    });

    // remove entries that contain only id field
    const payload = Object.entries(filteredValues).reduce(
      (res, [key, value]) => {
        if (
          typeof value !== "object" ||
          value instanceof File ||
          (value && Object.keys(value).length > 1)
        ) {
          return { ...res, [key]: value };
        }
        return res;
      },
      {}
    );

    const formData = new FormData();
    generateFormData(formData, {
      ...payload,
      declaration: filteredValues.declaration,
    });

    try {
      await submitApplication(formData);
      handleClose();
      // If submitted then navigate to my-request
      if (!applicationId) {
        navigate(myRequestRoute);
      }
    } catch (error) {}
  };

  return (
    <div className={!applicationId ? "h-100" : ""}>
      <Backdrop
        open={
          isApplicationDataLoading ||
          isConsumerDetailsLoading ||
          isApplicationDataFetching ||
          isConsumerDataFetching ||
          (!!applicationId && !customerDetails)
        }
      />
      {!applicationId && (
        <BreadCrumb
          title={t("Demand Extension")}
          pageTitle={t("Power Applications / Demand Alteration")}
        />
      )}
      <div className="stepper-container">
        <Stepper
          steps={steps}
          handleSubmit={handleSubmit}
          activeStepIndex={currentStep}
          setActiveStepIndex={setCurrentStep}
          validateDocument={validateDocument}
          isLoading={isLoading}
          formik={formik}
          isDraftDisabled
        />
      </div>
    </div>
  );
};

DemandExtension.propTypes = {
  applicationId: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};
export default withTranslation()(DemandExtension);
